import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';

const ToastContext = createContext(null);

const STILI = {
  success: { bg: 'bg-emerald-500', icona: 'CheckCircle' },
  warn:    { bg: 'bg-amber-500',   icona: 'AlertTriangle' },
  error:   { bg: 'bg-red-500',     icona: 'AlertCircle' },
};

const DURATA = { success: 4000, warn: 5500, error: 6000 };

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const contatore = useRef(0);
  const timer = useRef(new Map());

  const rimuovi = useCallback((id) => {
    const t = timer.current.get(id);
    if (t) {
      clearTimeout(t);
      timer.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const mostra = useCallback((tipo, messaggio) => {
    contatore.current += 1;
    const id = contatore.current;
    setToasts((prev) => [...prev.slice(-3), { id, tipo, messaggio }]);
    timer.current.set(id, setTimeout(() => rimuovi(id), DURATA[tipo] ?? 4500));
  }, [rimuovi]);

  useEffect(() => {
    const mappa = timer.current;
    return () => {
      mappa.forEach((t) => clearTimeout(t));
      mappa.clear();
    };
  }, []);

  const api = useMemo(() => ({
    success: (messaggio) => mostra('success', messaggio),
    warn:    (messaggio) => mostra('warn', messaggio),
    error:   (messaggio) => mostra('error', messaggio),
  }), [mostra]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed top-8 left-1/2 -translate-x-1/2 z-[400] flex flex-col items-center gap-2 pointer-events-none max-w-[92vw]"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const stile = STILI[t.tipo] ?? STILI.error;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 ${stile.bg} text-white`}
            >
              <Icon name={stile.icona} size={20} className="shrink-0" />
              <span className="text-sm font-black uppercase tracking-wider">{t.messaggio}</span>
              <button
                type="button"
                onClick={() => rimuovi(t.id)}
                aria-label="Chiudi notifica"
                className="ml-2 shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-all"
              >
                <Icon name="X" size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve essere usato dentro ToastProvider');
  return ctx;
};
